// ModalComponent.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import FadeIn from '../style/FadeIn.js';
import FadeOut from '../style/FadeOut.js';
import media from '../style/media.js';
import { color } from '../style/variables.js';

import { ModalStatus } from '../resource/modalConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { ButtonId } from '../resource/trackingConstants.js';

import CloseWhiteIconSource from '../../img/ic_close_white.svg';

const MODAL_MIN_HEIGHT = 440;
const MOBILE_MODAL_MIN_HEIGHT = 300;
const MODAL_BODY_WIDTH = 480;
const MODAL_WIDE_BODY_WIDTH = 880;
const MOBILE_MODAL_BODY_WIDTH = 320;
const ASIDE_WIDTH = 400;
const CLOSE_BUTTON_SIZE = 32;

export const ModalComponent = ({
  status = ModalStatus.HIDDEN,
  isClosable = false,
  isAnimationRequired = true,
  shouldShowBackground = true,
  children = null,
  trackingPayload = {},
  onCloseModal = () => null,
}) => {
  const { t } = useTranslation();
  const handleClickClose = event => {
    if (!isClosable) {
      return;
    }
    return onCloseModal(event);
  };
  const modalComponent = (
    <ModalWrapper>
      <CloseButton
        isClosable={isClosable}
        data-element_id={ButtonId.All.ButtonClose}
        data-tracking_payload={trackingPayload}
        onClick={handleClickClose}
      >
        <img
          src={CloseWhiteIconSource.src || CloseWhiteIconSource}
          alt={t('general_cancel', {
            ns: TranslationNamespace.GENERAL,
          })}
          width={CLOSE_BUTTON_SIZE}
          height={CLOSE_BUTTON_SIZE}
        />
      </CloseButton>
      <StyledModalComponent
        data-click_tracking_disabled
        modalStatus={status}
        isAnimationRequired={isAnimationRequired}
        onClick={event => event.stopPropagation()}
      >
        {children}
      </StyledModalComponent>
    </ModalWrapper>
  );
  if (shouldShowBackground) {
    return (
      <ClosableBackground
        modalStatus={status}
        data-element_id={ButtonId.All.ButtonClose}
        data-tracking_payload={trackingPayload}
        isAnimationRequired={isAnimationRequired}
        onClick={handleClickClose}
      >
        {modalComponent}
      </ClosableBackground>
    );
  }
  return modalComponent;
};

ModalComponent.propTypes = {
  status: PropTypes.string,
  isClosable: PropTypes.bool,
  isAnimationRequired: PropTypes.bool,
  shouldShowBackground: PropTypes.bool,
  children: PropTypes.node,
  trackingPayload: PropTypes.object,
  onCloseModal: PropTypes.func,
};

const transitionStyle = css`
  animation: 0.6s forwards
    ${({ modalStatus }) => ('closing' === modalStatus ? FadeOut : FadeIn)};
  animation: 0.6s forwards
    ${({ modalStatus }) => {
      switch (modalStatus) {
        case 'coming':
        case 'backing':
        case 'opening':
        case 'comingFromBottom':
          return FadeIn;
        case 'reversing':
        case 'leaving':
        case 'closing':
        case 'leavingToBottom':
          return FadeOut;
        default:
          return '';
      }
    }};
`;

const ClosableBackground = styled.div`
  ${({ isAnimationRequired }) => (isAnimationRequired ? transitionStyle : '')};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
`;

const ModalWrapper = styled.span`
  position: relative;
`;

const StyledModalComponent = styled.div`
  ${({ isAnimationRequired }) => (isAnimationRequired ? transitionStyle : '')};
  border-radius: 8px;
  min-height: min(${MODAL_MIN_HEIGHT}px, 80vh);
  max-height: 80vh;
  display: grid;
  grid-template-areas:
    'header aside'
    'body aside'
    'footer aside';
  grid-template-rows: auto 1fr auto;
  background-color: ${color.neutral[100]};
  overflow: hidden;
  ${media.tablet`
    grid-template-columns: auto auto;
  `};
  ${media.mobile`
    min-height: min(${MOBILE_MODAL_MIN_HEIGHT}px, 80vh);
  `};
`;

export const ModalHeader = styled.header`
  margin-bottom: 16px;
  padding: 40px 20px 0px;
  width: ${MODAL_BODY_WIDTH}px;
  grid-area: header;
  ${media.mobile`
    margin-bottom: 8px;
    max-width: ${MOBILE_MODAL_BODY_WIDTH}px;
    padding: 20px 16px 0px;
  `};
`;

export const ModalBody = styled.main`
  padding: 0px 20px 40px;
  width: ${MODAL_BODY_WIDTH}px;
  grid-area: body;
  overflow-y: auto;
  overflow-x: hidden;
  ${media.mobile`
    width: calc(100vw - ${16 * 2}px);
    max-width: ${MOBILE_MODAL_BODY_WIDTH}px;
    padding: 0px 16px 20px;
  `};
`;

export const ModalWideBody = styled(ModalBody)`
  width: ${MODAL_WIDE_BODY_WIDTH}px;
  ${media.tablet`
    width: ${MODAL_BODY_WIDTH}px;
  `};
`;

export const ModalAside = styled.aside`
  width: ${ASIDE_WIDTH}px;
  height: calc(100% + 1px);
  grid-area: aside;
  ${media.tablet`
    display: none;
  `};
`;

export const ModalFooter = styled.footer`
  grid-area: footer;
`;

const CloseButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 0px;
  right: -${16 + CLOSE_BUTTON_SIZE}px;
  width: ${CLOSE_BUTTON_SIZE}px;
  height: ${CLOSE_BUTTON_SIZE}px;
  display: ${({ isClosable }) => (isClosable ? 'block' : 'none')};
  > img {
    width: 100%;
    height: 100%;
  }
  ${media.mobile`
    display: none;
  `};
`;

export default ModalComponent;
